import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import '../../styles/modal/CoreModal.css';
import '../../styles/salesPopup/SalesPopup.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faShieldAlt, faClock, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import SalesPopupForm from '../../forms/salesPopup/SalesPopupForm';
import { validateSalesPopupForm } from '../../validations/SalesPopupValidation';
import SalesPopupService from '../../services/SalesPopupService';
import SalesPopupEventService from '../../services/SalesPopupEventService';
import SalesPopupSuccessModal from './SalesPopupSuccessModal';

const SalesPopup = () => {
  const location = useLocation();
  const [popupConfig, setPopupConfig] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const timeoutRef = useRef(null);
  const hasShownOnCurrentPageRef = useRef(false);

  // Verificar si estamos en la homepage
  const isHomepage = location.pathname === '/' || location.pathname === '';

  // Cargar configuración del popup una sola vez
  useEffect(() => {
    const fetchPopupConfig = async () => {
      try {
        const config = await SalesPopupService.getActivePopup();
        if (config && config.enabled) {
          setPopupConfig(config);
        } else {
          setPopupConfig(null);
        }
      } catch (error) {
        console.error('Error fetching sales popup config:', error);
        setPopupConfig(null);
      }
    };

    fetchPopupConfig();
  }, []);

  // Mostrar el popup cada vez que el usuario navega a la homepage
  useEffect(() => {
    // Limpiar timeout anterior si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Si estamos en la homepage y el popup está habilitado
    if (isHomepage && popupConfig && popupConfig.enabled) {
      // Resetear el flag cuando cambiamos de página
      hasShownOnCurrentPageRef.current = false;
      
      // Cerrar el popup si está abierto (para resetear el estado)
      setShowPopup(false);
      
      // Mostrar el popup después de un delay
      timeoutRef.current = setTimeout(() => {
        setShowPopup(true);
        hasShownOnCurrentPageRef.current = true;
        // Trackear impresión cuando se muestra el popup
        SalesPopupEventService.trackImpression();
      }, 2000); // 2 segundos de delay
    } else {
      // Si no estamos en la homepage, cerrar el popup
      setShowPopup(false);
      hasShownOnCurrentPageRef.current = false;
    }

    // Cleanup: limpiar timeout cuando el componente se desmonta o cambia la ubicación
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isHomepage, popupConfig]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleClose = () => {
    setShowPopup(false);
    // Trackear cierre cuando se cierra el popup sin acción
    SalesPopupEventService.trackClose();
  };

  const handleResetAndClose = () => {
    setFormData({
      name: '',
      phone: '',
    });
    setErrors({});
    setShowSuccessModal(false);
    handleClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateSalesPopupForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await SalesPopupService.createLead({
        name: formData.name,
        phone: formData.phone,
      });
      setShowSuccessModal(true);
    } catch (error) {
      setErrors({ submit: 'Error sending the form. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stopPropagation = (e) => e.stopPropagation();

  if (!showPopup || !popupConfig) {
    return null;
  }

  const coreApp = document.getElementById('core-app');
  if (!coreApp) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="core__modal sales-popup-modal" onClick={handleClose}>
      <div className="core__modal-content sales-popup-content" onClick={stopPropagation}>
        {/* Limited Time Badge */}
        <div className="sales-popup-badge">
          <FontAwesomeIcon icon={faClock} />
          <span>LIMITED TIME OFFER</span>
        </div>

        {/* Minimal Header with Close Button */}
        <div className="core__modal-header sales-popup-header">
          <button className="core__modal-close sales-popup-close" onClick={handleClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
        
        <div className="core__modal-body sales-popup-body">
          {/* Title and Subtitle */}
          <div className="sales-popup-content-header">
            <h2 className="sales-popup-title">{popupConfig.title}</h2>
            <p className="sales-popup-subtitle">{popupConfig.subtitle}</p>
          </div>

          {/* Trust Indicators */}
          <div className="sales-popup-trust-indicators">
            <div className="trust-item">
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>{popupConfig.trust_item_1 || 'Instant Discount'}</span>
            </div>
            <div className="trust-item">
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>{popupConfig.trust_item_2 || 'No Hidden Fees'}</span>
            </div>
            <div className="trust-item">
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>{popupConfig.trust_item_3 || 'Secure & Fast'}</span>
            </div>
          </div>

          <SalesPopupForm formData={formData} errors={errors} handleChange={handleChange} />
          {errors.submit && <span className="error-message">{errors.submit}</span>}
          
          <div className="core__modal-buttons">
            <button 
              className="button sales-popup-button" 
              onClick={async (e) => {
                // Trackear click en el botón
                await SalesPopupEventService.trackButtonClick();
                handleSubmit(e);
              }} 
              disabled={isSubmitting}
            >
              <span className="sales-popup-button-text">
                {isSubmitting ? 'Sending...' : popupConfig.button_text}
              </span>
            </button>
          </div>
          
          <div className="sales-popup-restrictions">
            *Restrictions Apply
          </div>
          
          <div className="privacy-assurance">
            <FontAwesomeIcon icon={faShieldAlt} />
            <span>Your information is secure. We respect your privacy.</span>
          </div>
        </div>
      </div>

      {showSuccessModal && (
        <SalesPopupSuccessModal 
          onClose={handleResetAndClose}
        />
      )}
    </div>,
    coreApp
  );
};

export default SalesPopup;

